import { useQueryClient } from '@tanstack/react-query';
import useNotifications from '../../../_lib/notifications/useNotifications';
import type { FieldValue } from '../../../form-engine/types';
import { saveQuoteDynamicFormFields } from '../../../api';
import { PubSub } from '../../../utils/eventUtils';
import { usePersona } from '../../hooks/usePersona';
import { isPrimeX, ProductTypes, type ProductType } from '../../../types';

type Props = {
  quoteId: string;
  productType?: ProductType;
};

const WARNING_MESSAGE =
  'Please navigate to the Quote Details page and click the blue Update Quote button to save all changes made within the Account Details. Pricing is subject to change, due to updated information.';
export const WARNING_KEY_PREFIX = 'Firmo Change Warning:';

type WarningParams = {
  quoteId: string;
  productType?: ProductType;
  isEligibleForPhase1Edit: boolean;
  personaIsAgency: boolean;
};

/**
 * For P100 and P250, shows warning regardless of persona. For PrimeX, only shows for agency users.
 */
function publishWarningIfNeeded({
  quoteId,
  productType,
  isEligibleForPhase1Edit,
  personaIsAgency,
}: WarningParams): void {
  // For P100 and P250, show warning regardless of persona. For PrimeX, only show for agency users.
  const isP100OrP250 =
    productType === ProductTypes.p100 || productType === ProductTypes.p250;
  const shouldShowWarning =
    isP100OrP250 || (isPrimeX(productType) && !isEligibleForPhase1Edit);

  if (shouldShowWarning && (isP100OrP250 || personaIsAgency)) {
    PubSub.publish(`${WARNING_KEY_PREFIX} ${quoteId}`, {
      message: WARNING_MESSAGE,
    });
  }
}

export function useSaveQuoteDynamicFields(props: Props) {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const persona = usePersona();
  const isEligibleForPhase1Edit =
    isPrimeX(props?.productType) && ProductTypes.zsp !== props?.productType;

  const saveQuoteDynamicFields = (payload: Record<string, FieldValue>) => {
    const data = { dynamicFields: payload };

    return saveQuoteDynamicFormFields(props.quoteId, { data }).then(
      (response: unknown) => {
        notifications.show({
          type: 'success',
          title: 'Answers saved',
          persist: false,
        });

        publishWarningIfNeeded({
          quoteId: props.quoteId,
          productType: props?.productType,
          isEligibleForPhase1Edit,
          personaIsAgency: persona.isAgency,
        });

        queryClient.invalidateQueries([
          'dynamic-form',
          { quoteId: props.quoteId },
        ]);

        return response;
      }
    );
  };

  return { saveQuoteDynamicFields };
}

export function useSaveQuoteLegacyQuestion(props: Props) {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const persona = usePersona();
  const isEligibleForPhase1Edit =
    isPrimeX(props?.productType) && ProductTypes.zsp !== props?.productType;

  const saveQuoteLegacyQuestions = (payload: Record<string, FieldValue>) => {
    const data = payload;

    return saveQuoteDynamicFormFields(props.quoteId, { data }).then(() => {
      notifications.show({
        type: 'success',
        title: 'Answers saved',
        persist: false,
      });

      publishWarningIfNeeded({
        quoteId: props.quoteId,
        productType: props?.productType,
        isEligibleForPhase1Edit,
        personaIsAgency: persona.isAgency,
      });

      queryClient.invalidateQueries([
        'dynamic-form',
        { quoteId: props.quoteId },
      ]);
    });
  };

  return { saveQuoteLegacyQuestions };
}

export function useSaveQuoteFirmographics(props: Props) {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const persona = usePersona();
  const isEligibleForPhase1Edit =
    isPrimeX(props?.productType) && ProductTypes.zsp !== props?.productType;

  const saveQuoteFirmographics = (payload: Record<string, FieldValue>) => {
    return saveQuoteDynamicFormFields(props.quoteId, {
      data: payload,
    }).then((response: unknown) => {
      notifications.show({
        type: 'success',
        title: 'Quote updated',
        persist: false,
      });

      publishWarningIfNeeded({
        quoteId: props.quoteId,
        productType: props?.productType,
        isEligibleForPhase1Edit,
        personaIsAgency: persona.isAgency,
      });

      queryClient.invalidateQueries([
        'quote-firmo-profile',
        { quoteId: props.quoteId },
      ]);

      return response;
    });
  };

  return { saveQuoteFirmographics };
}
